import React, { useState, useRef } from 'react';
import { uploadReportImage } from '../../services/reportBuilderService';
import { X, Palette, Type, Database, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import { DATA_SOURCE_OPTIONS, getPreset } from './dataSourcePresets';

const applyDataSource = (v, onChange) => {
  const p = getPreset(v);
  const upd = { dataSource: v };
  if (p) { upd.data = p.data; if (p.columns) upd.columns = p.columns; }
  else { upd.data = undefined; upd.columns = undefined; }
  onChange(upd);
};

const COLOR_PRESETS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#E11D48', '#374151', '#1E293B', '#059669'
];

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="flex-1 text-left">{title}</span>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {open && <div className="px-4 pb-3 space-y-3">{children}</div>}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none" />
);

const NumberInput = ({ value, onChange, min, max, step }) => (
  <input type="number" value={value ?? ''} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step || 1}
    className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none" />
);

const SelectInput = ({ value, onChange, options }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)}
    className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none bg-white">
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const Toggle = ({ value, onChange, label }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-gray-600">{label}</span>
    <button onClick={() => onChange(!value)}
      className={`w-9 h-5 rounded-full transition-colors ${value ? 'bg-blue-500' : 'bg-gray-300'}`}>
      <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${value ? 'translate-x-4.5 ml-auto mr-0.5' : 'translate-x-0.5'}`} />
    </button>
  </div>
);

const ColorPicker = ({ value, onChange, label }) => (
  <div>
    {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}
    <div className="flex flex-wrap gap-1.5">
      {COLOR_PRESETS.map(c => (
        <button key={c} onClick={() => onChange(c)}
          className={`w-6 h-6 rounded-md border-2 transition-all ${value === c ? 'border-gray-800 scale-110' : 'border-transparent hover:border-gray-300'}`}
          style={{ backgroundColor: c }} />
      ))}
      <div className="relative">
        <input type="color" value={value || '#3B82F6'} onChange={e => onChange(e.target.value)}
          className="w-6 h-6 rounded-md cursor-pointer border border-gray-200" />
      </div>
    </div>
  </div>
);

// Widget-specific property panels
const KPICardProps = ({ config, onChange }) => {
  const metrics = [
    { value: 'organic_traffic', label: 'Organic Traffic' },
    { value: 'keyword_rankings', label: 'Keyword Rankings' },
    { value: 'avg_position', label: 'Avg. Position' },
    { value: 'domain_authority', label: 'Domain Authority' },
    { value: 'backlinks_total', label: 'Total Backlinks' },
    { value: 'referring_domains', label: 'Referring Domains' },
    { value: 'site_health_score', label: 'Site Health Score' },
    { value: 'pages_indexed', label: 'Pages Indexed' },
    { value: 'ai_visibility_score', label: 'AI Visibility' },
    { value: 'ad_spend', label: 'Ad Spend' },
    { value: 'ad_clicks', label: 'Ad Clicks' },
    { value: 'ad_impressions', label: 'Ad Impressions' },
    { value: 'ctr', label: 'CTR' },
    { value: 'cpc', label: 'Avg. CPC' },
    { value: 'conversions', label: 'Conversions' },
    { value: 'conversion_rate', label: 'Conversion Rate' },
    { value: 'cost_per_conversion', label: 'Cost per Conversion' },
    { value: 'roas', label: 'ROAS' },
    { value: 'sessions', label: 'Sessions' },
    { value: 'users', label: 'Users' },
    { value: 'bounce_rate', label: 'Bounce Rate' },
    { value: 'gsc_clicks', label: 'Search Clicks' },
    { value: 'gsc_impressions', label: 'Search Impressions' },
    { value: 'gsc_ctr', label: 'Search CTR' },
    { value: 'gsc_avg_position', label: 'Search Avg. Position' }
  ];
  const formats = [
    { value: 'number', label: 'Number' },
    { value: 'currency', label: 'Currency ($)' },
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'decimal', label: 'Decimal' }
  ];
  return (
    <>
      <Section title="Content" icon={Database}>
        <Field label="Metric"><SelectInput value={config.metric} onChange={v => onChange({ metric: v })} options={metrics} /></Field>
        <Field label="Format"><SelectInput value={config.format} onChange={v => onChange({ format: v })} options={formats} /></Field>
        <Toggle label="Show Trend Arrow" value={config.showTrend !== false} onChange={v => onChange({ showTrend: v })} />
      </Section>
      <Section title="Style" icon={Palette}>
        <ColorPicker label="Accent Color" value={config.color} onChange={v => onChange({ color: v })} />
        <ColorPicker label="Title Color" value={config.titleColor} onChange={v => onChange({ titleColor: v })} />
      </Section>
    </>
  );
};

const LineChartProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Database}>
      <Field label="Title"><TextInput value={config.title} onChange={v => onChange({ title: v })} placeholder="Chart title" /></Field>
        <Field label="Data Source"><SelectInput value={config.dataSource || ""} onChange={v => applyDataSource(v, onChange)} options={DATA_SOURCE_OPTIONS} /></Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Toggle label="Smooth Line" value={config.smoothLine !== false} onChange={v => onChange({ smoothLine: v })} />
      <Toggle label="Show Grid" value={config.showGrid !== false} onChange={v => onChange({ showGrid: v })} />
      <Toggle label="Show Legend" value={config.showLegend !== false} onChange={v => onChange({ showLegend: v })} />
      <Toggle label="Show Area Fill" value={config.areaChart === true} onChange={v => onChange({ areaChart: v })} />
      <ColorPicker label="Title Color" value={config.titleColor} onChange={v => onChange({ titleColor: v })} />
    </Section>
  </>
);

const BarChartProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Database}>
      <Field label="Title"><TextInput value={config.title} onChange={v => onChange({ title: v })} placeholder="Chart title" /></Field>
        <Field label="Data Source"><SelectInput value={config.dataSource || ""} onChange={v => applyDataSource(v, onChange)} options={DATA_SOURCE_OPTIONS} /></Field>
      <Field label="Orientation">
        <SelectInput value={config.orientation} onChange={v => onChange({ orientation: v })}
          options={[{ value: 'vertical', label: 'Vertical' }, { value: 'horizontal', label: 'Horizontal' }]} />
      </Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Toggle label="Show Grid" value={config.showGrid !== false} onChange={v => onChange({ showGrid: v })} />
      <Toggle label="Show Legend" value={config.showLegend !== false} onChange={v => onChange({ showLegend: v })} />
      <Field label="Bar Radius"><NumberInput value={config.barRadius || 4} onChange={v => onChange({ barRadius: v })} min={0} max={20} /></Field>
      <ColorPicker label="Title Color" value={config.titleColor} onChange={v => onChange({ titleColor: v })} />
    </Section>
  </>
);

const PieChartProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Database}>
      <Field label="Title"><TextInput value={config.title} onChange={v => onChange({ title: v })} placeholder="Chart title" /></Field>
        <Field label="Data Source"><SelectInput value={config.dataSource || ""} onChange={v => applyDataSource(v, onChange)} options={DATA_SOURCE_OPTIONS} /></Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Toggle label="Donut Style" value={config.donut === true} onChange={v => onChange({ donut: v })} />
      {config.donut && <Field label="Inner Radius"><NumberInput value={config.innerRadius || 50} onChange={v => onChange({ innerRadius: v })} min={20} max={80} /></Field>}
      <Toggle label="Show Labels" value={config.showLabels !== false} onChange={v => onChange({ showLabels: v })} />
      <Toggle label="Show Legend" value={config.showLegend !== false} onChange={v => onChange({ showLegend: v })} />
      <Field label="Padding Angle"><NumberInput value={config.paddingAngle || 2} onChange={v => onChange({ paddingAngle: v })} min={0} max={10} /></Field>
      <ColorPicker label="Title Color" value={config.titleColor} onChange={v => onChange({ titleColor: v })} />
    </Section>
  </>
);

const DataTableProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Database}>
      <Field label="Title"><TextInput value={config.title} onChange={v => onChange({ title: v })} placeholder="Table title" /></Field>
        <Field label="Data Source"><SelectInput value={config.dataSource || ""} onChange={v => applyDataSource(v, onChange)} options={DATA_SOURCE_OPTIONS} /></Field>
      <Field label="Rows to Show"><NumberInput value={config.rowCount || 10} onChange={v => onChange({ rowCount: v })} min={3} max={50} /></Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Toggle label="Striped Rows" value={config.striped !== false} onChange={v => onChange({ striped: v })} />
      <Toggle label="Show Borders" value={config.bordered !== false} onChange={v => onChange({ bordered: v })} />
      <Toggle label="Compact Mode" value={config.compact === true} onChange={v => onChange({ compact: v })} />
      <Field label="Header Color">
        <SelectInput value={config.headerStyle || 'dark'} onChange={v => onChange({ headerStyle: v })}
          options={[{ value: 'dark', label: 'Dark' }, { value: 'blue', label: 'Blue' }, { value: 'light', label: 'Light' }, { value: 'minimal', label: 'Minimal' }]} />
      </Field>
      <ColorPicker label="Title Color" value={config.titleColor} onChange={v => onChange({ titleColor: v })} />
    </Section>
  </>
);

const TextBlockProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Type}>
      <Field label="Text"><textarea value={config.text || ''} onChange={e => onChange({ text: e.target.value })} rows={4} placeholder="Enter text..."
        className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none resize-none" /></Field>
      <Field label="Variant">
        <SelectInput value={config.variant || 'paragraph'} onChange={v => onChange({ variant: v })}
          options={[{ value: 'h1', label: 'Heading 1' }, { value: 'h2', label: 'Heading 2' }, { value: 'h3', label: 'Heading 3' }, { value: 'paragraph', label: 'Paragraph' }, { value: 'caption', label: 'Caption' }]} />
      </Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Field label="Alignment">
        <SelectInput value={config.align || 'left'} onChange={v => onChange({ align: v })}
          options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }]} />
      </Field>
      <ColorPicker label="Text Color" value={config.textColor} onChange={v => onChange({ textColor: v })} />
      <ColorPicker label="Background" value={config.bgColor} onChange={v => onChange({ bgColor: v })} />
    </Section>
  </>
);

const ScoreGaugeProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Database}>
      <Field label="Title"><TextInput value={config.title} onChange={v => onChange({ title: v })} placeholder="Score label" /></Field>
      <Field label="Value"><NumberInput value={config.value || 87} onChange={v => onChange({ value: v })} min={0} max={1000} /></Field>
      <Field label="Max Value"><NumberInput value={config.maxValue || 100} onChange={v => onChange({ maxValue: v })} min={1} max={1000} /></Field>
      <Field label="Subtitle"><TextInput value={config.subtitle} onChange={v => onChange({ subtitle: v })} placeholder="e.g. out of 100" /></Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Toggle label="Auto Color (green/yellow/red)" value={config.autoColor === true} onChange={v => onChange({ autoColor: v })} />
      {!config.autoColor && <ColorPicker label="Gauge Color" value={config.color} onChange={v => onChange({ color: v })} />}
      <ColorPicker label="Title Color" value={config.titleColor} onChange={v => onChange({ titleColor: v })} />
    </Section>
  </>
);

const ComparisonProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Database}>
      <Field label="Title"><TextInput value={config.title} onChange={v => onChange({ title: v })} placeholder="Comparison title" /></Field>
      <Field label="Current Value"><NumberInput value={config.currentValue ?? ''} onChange={v => onChange({ currentValue: v })} /></Field>
      <Field label="Previous Value"><NumberInput value={config.previousValue ?? ''} onChange={v => onChange({ previousValue: v })} /></Field>
      <Field label="Current Label"><TextInput value={config.currentLabel} onChange={v => onChange({ currentLabel: v })} placeholder="This Month" /></Field>
      <Field label="Previous Label"><TextInput value={config.previousLabel} onChange={v => onChange({ previousLabel: v })} placeholder="Last Month" /></Field>
      <Field label="Format">
        <SelectInput value={config.format || 'number'} onChange={v => onChange({ format: v })}
          options={[{ value: 'number', label: 'Number' }, { value: 'currency', label: 'Currency' }, { value: 'percentage', label: 'Percentage' }]} />
      </Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <ColorPicker label="Accent Color" value={config.color} onChange={v => onChange({ color: v })} />
      <ColorPicker label="Title Color" value={config.titleColor} onChange={v => onChange({ titleColor: v })} />
    </Section>
  </>
);

const DividerProps = ({ config, onChange }) => (
  <>
    <Section title="Content" icon={Settings}>
      <Field label="Label (optional)"><TextInput value={config.label} onChange={v => onChange({ label: v })} placeholder="Section label" /></Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Field label="Line Style">
        <SelectInput value={config.style || 'solid'} onChange={v => onChange({ style: v })}
          options={[{ value: 'solid', label: 'Solid' }, { value: 'dashed', label: 'Dashed' }, { value: 'dotted', label: 'Dotted' }]} />
      </Field>
      <Field label="Thickness"><NumberInput value={config.thickness || 1} onChange={v => onChange({ thickness: v })} min={1} max={5} /></Field>
      <ColorPicker label="Color" value={config.color} onChange={v => onChange({ color: v })} />
    </Section>
  </>
);

const ImageBlockProps = ({ config, onChange }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadReportImage(file);
      onChange({ src: result.url });
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
  <>
    <Section title="Content" icon={Database}>
      <Field label="Upload Image">
        <div className="space-y-2">
          <input type="file" ref={fileInputRef} accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp" onChange={handleFileUpload} className="hidden" />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full px-3 py-2 text-sm border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {uploading ? (<><span className="animate-spin h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full"></span> Uploading...</>) : (<><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> Choose Image</>)}
          </button>
          <p className="text-xs text-gray-400 text-center">PNG, JPG, GIF, SVG, WebP (max 2MB)</p>
        </div>
      </Field>
      <Field label="Or Image URL"><TextInput value={config.src} onChange={v => onChange({ src: v })} placeholder="https://..." /></Field>
      {config.src && (<div className="mx-3 mb-2 p-2 bg-gray-50 rounded border"><img src={config.src} alt="Preview" className="max-h-20 mx-auto rounded" onError={e => e.target.style.display='none'} /></div>)}
      <Field label="Alt Text"><TextInput value={config.alt} onChange={v => onChange({ alt: v })} placeholder="Image description" /></Field>
    </Section>
    <Section title="Style" icon={Palette}>
      <Field label="Fit">
        <SelectInput value={config.fit || 'contain'} onChange={v => onChange({ fit: v })}
          options={[{ value: 'contain', label: 'Contain' }, { value: 'cover', label: 'Cover' }, { value: 'fill', label: 'Fill' }]} />
      </Field>
      <ColorPicker label="Background" value={config.bgColor} onChange={v => onChange({ bgColor: v })} />
    </Section>
  </>
);
};

const PANEL_MAP = {
  kpi_card: KPICardProps,
  line_chart: LineChartProps,
  bar_chart: BarChartProps,
  pie_chart: PieChartProps,
  data_table: DataTableProps,
  text_block: TextBlockProps,
  score_gauge: ScoreGaugeProps,
  comparison_card: ComparisonProps,
  divider: DividerProps,
  image_block: ImageBlockProps
};

const PropertiesPanel = ({ widget, onUpdateConfig, onClose }) => {
  if (!widget) return null;

  const PanelComponent = PANEL_MAP[widget.type];
  const handleChange = (updates) => {
    onUpdateConfig(widget.id, updates);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Widget Properties</h3>
          <p className="text-xs text-gray-400 mt-0.5">{widget.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* Common title field for most widgets */}
        {widget.type !== 'divider' && widget.type !== 'image_block' && (
          <Section title="General" icon={Settings} defaultOpen={false}>
            <Field label="Widget Title">
              <TextInput value={widget.config.title} onChange={v => handleChange({ title: v })}
                placeholder={widget.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} />
            </Field>
          </Section>
        )}
        {PanelComponent ? (
          <PanelComponent config={widget.config || {}} onChange={handleChange} />
        ) : (
          <div className="p-4 text-sm text-gray-400 text-center">
            No properties available for this widget type.
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPanel;
